import React, { useEffect, useMemo, useRef, useState } from 'react';
import Swal from '../../../../helpers/swal';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import clinicService from '../../../../../services/clinic.service';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

function isPdfMedia(item) {
    const type = item?.type?.toLowerCase() || '';
    return type === 'application/pdf' || type === 'application/x-pdf' || item?.name?.toLowerCase().endsWith('.pdf');
}

function MediaListItem({ item, updateMediaList }) {
    const [isCopied, setIsCopied] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
    const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
    const [pdfThumbFailed, setPdfThumbFailed] = useState(false);
    const viewportRef = useRef(null);
    const imageRef = useRef(null);

    const isPdf = isPdfMedia(item);
    const thumbSrc = isPdf ? item.thumbnailPath || item.path : item.path;

    const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(value * 100) / 100));

    const syncNaturalSize = (image) => {
        if (!image?.naturalWidth || !image?.naturalHeight) {
            return;
        }

        setNaturalSize({
            width: image.naturalWidth,
            height: image.naturalHeight,
        });
    };

    const measureViewport = () => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }

        const styles = window.getComputedStyle(viewport);
        const padX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
        const padY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
        setViewportSize({
            width: Math.max(viewport.clientWidth - padX, 0),
            height: Math.max(viewport.clientHeight - padY, 0),
        });
    };

    const handleOpenPreview = () => {
        setZoom(1);
        setNaturalSize({ width: 0, height: 0 });
        setViewportSize({ width: 0, height: 0 });
        setShowPreview(true);
    };

    const handleClosePreview = () => {
        setShowPreview(false);
        setZoom(1);
        setNaturalSize({ width: 0, height: 0 });
        setViewportSize({ width: 0, height: 0 });
    };

    const handleZoomIn = () => setZoom((current) => clampZoom(current + ZOOM_STEP));
    const handleZoomOut = () => setZoom((current) => clampZoom(current - ZOOM_STEP));
    const handleZoomReset = () => setZoom(1);

    const handlePreviewWheel = (event) => {
        if (isPdf || (!event.ctrlKey && !event.metaKey)) {
            return;
        }

        event.preventDefault();
        const delta = event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
        setZoom((current) => clampZoom(current + delta));
    };

    const handleImageLoad = (event) => {
        syncNaturalSize(event.currentTarget);
    };

    const handlePreviewEntered = () => {
        if (isPdf) {
            return;
        }
        measureViewport();
        syncNaturalSize(imageRef.current);
    };

    useEffect(() => {
        if (!showPreview || isPdf) {
            return undefined;
        }

        const viewport = viewportRef.current;
        if (!viewport) {
            return undefined;
        }

        measureViewport();
        syncNaturalSize(imageRef.current);

        const observer = new ResizeObserver(() => {
            measureViewport();
        });
        observer.observe(viewport);

        return () => observer.disconnect();
    }, [showPreview, isPdf]);

    const displaySize = useMemo(() => {
        if (isPdf || !naturalSize.width || !naturalSize.height || !viewportSize.width || !viewportSize.height) {
            return null;
        }

        const fitScale = Math.min(
            viewportSize.width / naturalSize.width,
            viewportSize.height / naturalSize.height,
            1
        );

        return {
            width: Math.max(1, Math.round(naturalSize.width * fitScale * zoom)),
            height: Math.max(1, Math.round(naturalSize.height * fitScale * zoom)),
        };
    }, [isPdf, naturalSize, viewportSize, zoom]);

    const stageSize = useMemo(() => {
        if (!displaySize || !viewportSize.width || !viewportSize.height) {
            return null;
        }

        return {
            width: Math.max(displaySize.width, viewportSize.width),
            height: Math.max(displaySize.height, viewportSize.height),
        };
    }, [displaySize, viewportSize]);

    const handleDelete = () => {
        Swal.fire({
            title: 'Delete Media?',
            html: `<div class="text-center small">
                     <p class="small mb-0">Are you sure you want to delete <strong>${item.name}</strong>?</p>
                   </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete it!`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-danger btn-sm me-2',
                cancelButton: 'btn btn-secondary btn-sm',
            },
            buttonsStyling: false,
            showLoaderOnConfirm: true,
            preConfirm: () =>
                clinicService
                    .deleteMedia(item.clinicId, [item.id])
                    .then(() => true)
                    .catch((error) => {
                        Swal.showValidationMessage(`Delete failed: ${error.message}`);
                    }),
        }).then((result) => {
            if (result.isConfirmed) {
                updateMediaList();
            }
        });
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(item.path);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = item.path;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
    };

    return (
        <div className="clinic-media-card">
            <div className="notifications-panel">
                <div className="notifications-panel__header clinic-media-card__header">
                    <span className="text-truncate small fw-semibold">{item.name}</span>
                    <ActionTooltipButton
                        tooltip="Delete media"
                        tooltipId={`delete-media-${item.id}`}
                        variant="outline-danger"
                        size="sm"
                        onClick={handleDelete}
                    >
                        <i className="bi bi-trash"></i>
                    </ActionTooltipButton>
                </div>

                <div className="notifications-panel__body">
                    <button
                        type="button"
                        className={`clinic-media-card__thumb${isPdf ? ' clinic-media-card__thumb--pdf' : ''}`}
                        onClick={handleOpenPreview}
                        title={isPdf ? 'Preview PDF' : 'Preview image'}
                        aria-label={`Preview ${item.name}`}
                    >
                        {isPdf ? (
                            item.thumbnailPath && !pdfThumbFailed ? (
                                <img
                                    src={item.thumbnailPath}
                                    alt=""
                                    loading="lazy"
                                    onError={() => setPdfThumbFailed(true)}
                                />
                            ) : (
                                <i className="bi bi-file-earmark-pdf clinic-media-card__pdf-icon" aria-hidden="true"></i>
                            )
                        ) : (
                            <img
                                src={thumbSrc}
                                alt={item.name}
                                loading="lazy"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src =
                                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="%23adb5bd"%3E%3Cpath d="M8 4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1h4a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4V4z"/%3E%3C/svg%3E';
                                }}
                            />
                        )}
                        <span className="clinic-media-card__thumb-overlay" aria-hidden="true">
                            <i className={`bi ${isPdf ? 'bi-eye' : 'bi-zoom-in'}`}></i>
                        </span>
                    </button>

                    <div className="clinic-media-card__meta">
                        <span className="clinic-media-meta-badge">
                            <i className="bi bi-calendar3"></i>
                            {item.modifiedAt}
                        </span>
                        <span className="clinic-media-meta-badge clinic-media-meta-badge--type">
                            <i className={`bi ${isPdf ? 'bi-file-earmark-pdf' : 'bi-file-earmark'}`}></i>
                            {item.type}
                        </span>
                        <span className="clinic-media-meta-badge clinic-media-meta-badge--size">
                            <i className="bi bi-hdd"></i>
                            {formatFileSize(item.sizeBytes)}
                        </span>
                    </div>

                    <div className="clinic-media-card__path">
                        <input type="text" className="form-control form-control-sm bg-light" value={item.path} readOnly aria-label="Media path" />
                        <ActionTooltipButton
                            tooltip={isCopied ? 'Copied!' : 'Copy path'}
                            tooltipId={`copy-media-${item.id}`}
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleCopy}
                        >
                            <i className={`bi ${isCopied ? 'bi-check2 text-success' : 'bi-copy'}`}></i>
                        </ActionTooltipButton>
                    </div>
                </div>
            </div>

            <Modal
                show={showPreview}
                onHide={handleClosePreview}
                onEntered={handlePreviewEntered}
                size="xl"
                centered
                className="clinic-media-preview-modal"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="clinic-media-preview-modal__title">
                        <i className={`bi ${isPdf ? 'bi-file-earmark-pdf' : 'bi-image'} me-2`}></i>
                        <span className="text-truncate">{item.name}</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {isPdf ? (
                        <>
                            <div className="clinic-media-preview-modal__toolbar">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    href={item.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <i className="bi bi-box-arrow-up-right me-1"></i>
                                    Open in new tab
                                </Button>
                            </div>
                            <div className="clinic-media-preview-modal__body clinic-media-preview-modal__body--pdf">
                                <iframe
                                    title={`PDF preview: ${item.name}`}
                                    src={`${item.path}#toolbar=1&navpanes=0`}
                                    className="clinic-media-preview-modal__pdf"
                                />
                            </div>
                            <p className="clinic-media-preview-modal__hint mb-0">
                                Use the built-in PDF controls to scroll and zoom.
                            </p>
                        </>
                    ) : (
                        <>
                            <div className="clinic-media-preview-modal__toolbar">
                                <ActionTooltipButton
                                    tooltip="Zoom out"
                                    tooltipId={`zoom-out-${item.id}`}
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleZoomOut}
                                    disabled={zoom <= MIN_ZOOM}
                                >
                                    <i className="bi bi-zoom-out"></i>
                                </ActionTooltipButton>
                                <button
                                    type="button"
                                    className="clinic-media-preview-modal__zoom-label"
                                    onClick={handleZoomReset}
                                    title="Reset zoom"
                                >
                                    {Math.round(zoom * 100)}%
                                </button>
                                <ActionTooltipButton
                                    tooltip="Zoom in"
                                    tooltipId={`zoom-in-${item.id}`}
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleZoomIn}
                                    disabled={zoom >= MAX_ZOOM}
                                >
                                    <i className="bi bi-zoom-in"></i>
                                </ActionTooltipButton>
                                <ActionTooltipButton
                                    tooltip="Reset zoom"
                                    tooltipId={`zoom-reset-${item.id}`}
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleZoomReset}
                                    disabled={zoom === 1}
                                >
                                    <i className="bi bi-arrows-angle-contract"></i>
                                </ActionTooltipButton>
                            </div>
                            <div
                                className="clinic-media-preview-modal__body"
                                ref={viewportRef}
                                onWheel={handlePreviewWheel}
                            >
                                <div
                                    className="clinic-media-preview-modal__stage"
                                    style={
                                        stageSize
                                            ? {
                                                  width: `${stageSize.width}px`,
                                                  height: `${stageSize.height}px`,
                                              }
                                            : undefined
                                    }
                                >
                                    <img
                                        ref={imageRef}
                                        src={item.path}
                                        alt={item.name}
                                        onLoad={handleImageLoad}
                                        draggable={false}
                                        style={
                                            displaySize
                                                ? {
                                                      width: `${displaySize.width}px`,
                                                      height: `${displaySize.height}px`,
                                                  }
                                                : {
                                                      maxWidth: '100%',
                                                      maxHeight: '100%',
                                                      width: 'auto',
                                                      height: 'auto',
                                                  }
                                        }
                                    />
                                </div>
                            </div>
                            <p className="clinic-media-preview-modal__hint mb-0">
                                Use the controls to zoom, or hold Ctrl / ⌘ and scroll.
                            </p>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default MediaListItem;
